import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import Api from '../utils/Api';
import { useError } from './ErrorContext';
import { useLoading } from './LoadingContext';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useRoutePath } from '../utils/getRoutePath';

const EstimateJobTagContext = createContext();

export const useEstimateJobTag = () => {
  const context = useContext(EstimateJobTagContext);
  if (!context) {
    throw new Error('useEstimateJobTag must be used within a EstimateJobTagProvider');
  }
  return context;
};

export const EstimateJobTagProvider = ({ children }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { hideLoading } = useLoading();
  const { user, getToken } = useAuth();
  const [api, setApi] = useState(null);
  const { handleApiError, formErrors, clearErrors } = useError();
  const [estimateJobTags, setEstimateJobTags] = useState([]);
  const segments = location.pathname.split('/');
  const id = segments.pop();
  const [paginate, setPaginate] = useState(true);
  const getRoutePath = useRoutePath();
  const initialized = location.pathname === getRoutePath('/estimate-job-tags');

  const getEstimateJobTags = useCallback(async (page = 1, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const searchParams = new URLSearchParams(window.location.search);
      let query = '';
      if (filters?.q) {
        query = '&q=' + filters?.q;
      } else {
        const q = searchParams.get('q') || '';
        query = '&q=' + q;
      }
      if (filters?.active) {
        query += '&active=' + filters?.active;
      } else {
        const active = searchParams.get('active') || '';
        query += '&active=' + active;
      }
       
      let response = await apiInstance.call('estimate-jobs-tags?page=' + page + '&paginate=' + paginate + '' + query, 'GET');
      if (filters?.all) {
        query = '';
        setPaginate(false);
        response = await apiInstance.call('estimate-jobs-tags');
      }

      if (response.status === 200) {
        setPaginate(response.data.paginate);
        setEstimateJobTags(response.data.estimate_job_tags || []);
        hideLoading();
        if (filters) {
          return response.data.estimate_job_tags;
        }
      } else {
        hideLoading();
        handleApiError(response.error, {
          showAlert: false,
          redirectOn401: true,
          customMessage: 'Failed to fetch estimate job tags'
        });
      }
    } catch (error) {
      hideLoading();
    }
  }, [handleApiError, hideLoading, paginate, getToken]);

  const getEstimateJobTagByIdApi = useCallback(async (id, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call(`estimate-jobs-tags/${id}`, 'GET', null, true, true);

    if (response.status === 200) {
      let estimateJobTags = { 'data': [response.data.estimate_job_tag] };
      setEstimateJobTags(estimateJobTags || []);
    }
  }, [getToken]);

  useEffect(() => {
    const handleLoad = async () => {
      const currentPath = window.location.pathname;

      if (currentPath.includes(getRoutePath('/estimate-job-tags/edit/' + id))) {
        if (getToken()) {
          const apiInstance = Api(() => getToken());
          setApi(apiInstance);
          await getEstimateJobTagByIdApi(id);
        }
      }
    };

    window.addEventListener('load', handleLoad);
    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, [getRoutePath, id, getToken, getEstimateJobTagByIdApi, searchParams]);

  useEffect(() => {
    const initializeApi = async () => {
      try {
        const apiInstance = Api(() => getToken());
        setApi(apiInstance);

        if (getToken()) {
          let page = parseInt(searchParams.get('page')) || 1;
          await getEstimateJobTags(page);
        }
      } catch (error) {
        // Handle error
      }
    };

    if (initialized) {
      initializeApi();
    }
  }, [getToken, getEstimateJobTags, searchParams, initialized]);

  const addEstimateJobTag = async (tag) => {
    if (!api) {
      return;
    }

    const formData = new FormData();
    Object.keys(tag).forEach(key => {
      if (tag[key] !== null) {
        formData.append(key, tag[key]);
      }
    });
    formData.append('company_id', user?.current_company?.id);
    formData.append('user_id', user?.id);

    try {
      const response = await api.call('estimate-jobs-tags', 'POST', formData, true, true);
      
      if (response.status === 200) {
        const estimateJobTags = response.data.estimate_job_tags;
        setEstimateJobTags(estimateJobTags);
        return { status: response.status };
      } else {
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
          return handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to add estimate job tag'
          });
        }
      }
    } catch (error) {
      return handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to add estimate job tag'
      });
    }
  };

  const getEstimateJobTagById = (id) => {
    let estimateJobTagData = estimateJobTags?.data?.find(tag => {
      return Number(tag.id) === Number(id);
    });
    return estimateJobTagData;
  };

  const updateEstimateJobTag = async (id, updatedTag) => {
    if (!api) {
      return;
    }

    const formData = new FormData();
    Object.keys(updatedTag).forEach(key => {
      if (updatedTag[key] !== null) {
        formData.append(key, updatedTag[key]);
      }
    });
    formData.append('company_id', user?.current_company?.id);
    formData.append('user_id', user?.id);

    try {
      const response = await api.call(`estimate-jobs-tags/${id}`, 'POST', formData, true, true);
      if (response.status === 200) {
        const updatedEstimateJobTags = estimateJobTags?.data?.map(tag =>
          Number(tag.id) === Number(id) ? {
            ...response.data.estimate_job_tag,
          } : tag
        );
        setEstimateJobTags(updatedEstimateJobTags);
        return { status: response.status };
      } else {
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
          return handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to update estimate job tag'
          });
        }
      }
    } catch (error) {
      return handleApiError(error, {
        showAlert: false,
        redirectOn401: true,
        customMessage: 'Failed to update estimate job tag'
      });
    }
  };

  const deleteEstimateJobTag = async (id) => {
    if (!api) {
      return;
    }

    try {
      const response = await api.call(`estimate-jobs-tags/${id}`, 'DELETE', null, true);
      if (response.status === 200) {
        navigate(getRoutePath('/estimate-job-tags'));
        return { status: response.status };
      } else {
        return handleApiError(response.error, {
          showAlert: false,
          redirectOn401: true,
          customMessage: 'Failed to delete estimate job tag'
        });
      }
    } catch (error) {
      return handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to delete estimate job tag'
      });
    }
  };

  return (
    <EstimateJobTagContext.Provider
      value={{
        estimateJobTags,
        addEstimateJobTag,
        getEstimateJobTags: (page, filters) => getEstimateJobTags(page, filters),
        getEstimateJobTagById,
        updateEstimateJobTag,
        deleteEstimateJobTag,
        formErrors,
        paginate,
        clearErrors
      }}
    >
      {children}
    </EstimateJobTagContext.Provider>
  );
}; 